/**
 * 🆕 **信用卡账单明细行**的装配（批 `106-A` · `DR-061`）
 *
 * 依据：`B域PRD_信用卡账单明细与还款优惠_增补` §3（账单视图）· §4（拆分合并）· **AC-B2.37~B2.41** ·
 * 规格 **§8**（信用卡：家盈账单必须与银行账单对得上）· `DR-060` §6（退款 vs 还款分流）·
 * 铁律 **24**（同一机制一处实现）。
 *
 * ## 🔴 这个文件只回答一件事：**把一堆腿装配成"银行流水那样的一行一行"**
 *
 * 两个判据如果各处实现一遍，用户会立刻看到不一致：
 *
 * | 判据 | 若两处各写一遍的后果 |
 * |---|---|
 * | **窗口包含**（半开 / 未出闭区间） | 同一笔消费在卡面算进本期、在明细里却不在 ⇒ 明细与应还对不上 |
 * | **拆分合并**（同账户同向 ≥ 2 ⇒ 一行） | 银行流水 1 行、家盈 3 行 ⇒ 用户逐行对账时对不上 |
 *
 * ⇒ 二者都收在本文件，**账单明细与账户流水共用**（`AC-B2.40` / `AC-B2.64`）。
 *
 * ## 🔴 「合并不合并」的判据为什么按 `direction` 分组
 *
 * 用户诉求是「**和银行流水对应**」—— 银行一笔入账就是一行。家盈里"银行的一行"
 * 对应大同小异的几种多腿形态，只有**拆分**是"银行看也是一笔"：
 *
 * | 形态 | 该账户上的腿 | 合并 | 理由 |
 * |---|---|:---:|---|
 * | 拆分 | N 条**同向** | ✅ | 银行账单上它是一笔总额（用户的原话场景） |
 * | 转账 | 1 条（另一条在对方账户） | ❌ | 每条本来就是一行 |
 * | 工资条 | 父 `in` + 子 `out`（**异向**） | ❌ | 方向不同 = 资金流向不同，合成一行没有语义（`DR-004`） |
 * | 分期还本期 | 本金 `in` + 手续费 `out`（**异向**） | ❌ | 同上；且手续费是另一笔支出 |
 * | 还款 | 1 条 `in` | ❌ | 单腿，不触发判定 |
 *
 * ⇒ `direction` 是唯一需要的分组维度 —— **不需要知道"这笔是不是拆分"**，
 *  因此不会出现"先判形态再决定要不要合并"的第二份形态认定。
 *
 * ## ⚠️ 本文件**不做**什么
 *
 * - **不碰库、不取时钟** —— 腿由调用方（服务端取数层）传入；
 * - **不算金额以外的事实** —— `kind` 的判定依赖调用方已打好的 `refund` / `waiver` 标记
 *   （退款判定唯一实现在 `nucleus/split.ts` 的 `isRefundTxn`，本文件只消费它）；
 * - **不排序** —— 行序 = 输入序（服务端按 `date DESC, createdAt DESC` 查），
 *   这里不重排（避免"分页时顺序莫名变化"，其同族坑见 `ORDER BY id`）。
 */

import type { Cents } from '../types/brand.js';
import { LegDirection } from '../enums/journal.js';
import { sumCents } from './money.js';

/** 明细行的**四类归属**（`AC-B2.34`：四类之和能对上该期账户余额变动） */
export const BillLegRowKind = {
  /** 消费（`out`） */
  Spend: 'spend',
  /** 退款（退款形态的 `in` —— 冲减本期消费） */
  Refund: 'refund',
  /** 还款（还款转账的 `in` —— 不冲减本期） */
  Repayment: 'repayment',
  /** 还款优惠（`waiver` 调整腿；批 `106-B` 起有实际数据） */
  Waiver: 'waiver',
} as const;
export type BillLegRowKind = (typeof BillLegRowKind)[keyof typeof BillLegRowKind];

/** 明细视图使用的四类取值清单（与上一份常量同源，供契约 / 端侧遍历） */
export const BillLegRowKinds: readonly BillLegRowKind[] = [
  BillLegRowKind.Spend,
  BillLegRowKind.Refund,
  BillLegRowKind.Repayment,
  BillLegRowKind.Waiver,
];

/** 装配输入：**一条腿**（服务端取数层已打好的判定标记） */
export interface BillLegSource {
  /** 所属交易（合并的分组键之一） */
  transactionId: string;
  /** 账本本地日历日 `YYYY-MM-DD` */
  date: string;
  direction: LegDirection;
  /** 该腿金额（**恒正**，整数分） */
  amount: Cents;
  categoryId: string | null;
  /** 分类中文名（服务端 JOIN 出来；🔴 端侧不得按 id 自己 mapping —— 铁律 18/103） */
  label: string | null;
  /** 腿级备注（`legs.note`）；为空时服务端可回填交易级备注 */
  note: string | null;
  /** 🔴 **是否退款**（服务端按 `isRefundTxn` 判定后传入 —— 退款认定不在本文件） */
  refund?: boolean;
  /** 🔴 **是否还款优惠腿**（`legs.linked_waiver_id` 非空；批 `106-B` 起由服务端传入） */
  waiver?: boolean;
}

/** 合并行的子项（**只有合并行才有**；单腿行不出发这个键 —— 可选键剔键纪律） */
export interface BillLegChildView {
  categoryId: string | null;
  label: string | null;
  amount: number;
  note: string | null;
}

/** 明细行（已按「交易 × 方向」合并） */
export interface BillLegRowView {
  transactionId: string;
  date: string;
  kind: BillLegRowKind;
  direction: LegDirection;
  /** 合并后总额（拆分 = Σ 同向腿；非拆分 = 该腿金额） */
  amount: number;
  /** 全部子腿同分类 ⇒ 该分类；否则 `null` + 端侧展示「拆分 N 项」 */
  categoryId: string | null;
  label: string | null;
  note: string | null;
  /** 合并的腿数；`1` = 未合并 */
  legCount: number;
  children?: BillLegChildView[];
}

/**
 * 四条腿身份的判定顺序（**互斥、按序**）。
 *
 * 🔴 `waiver` 先于 `refund`：优惠腿的形态是「`in` + 无分类 + 不计收支」，
 * 与还款完全同形，只有 `linked_waiver_id` 能区分 ⇒ 服务端必须先把这个标记打上去。
 * 🔴 判据**不用 `note` 文案**（用户可改备注）。
 */
function kindOf(source: BillLegSource): BillLegRowKind {
  if (source.waiver === true) return BillLegRowKind.Waiver;
  if (source.refund === true) return BillLegRowKind.Refund;
  if (source.direction === LegDirection.Out) return BillLegRowKind.Spend;
  return BillLegRowKind.Repayment;
}

/**
 * **拆分合并**（`AC-B2.37`）—— 账单明细与账户流水共用的唯一实现。
 *
 * @param sources 已按期望行序排好（服务端 `date DESC, createdAt DESC`）
 * @returns 行序 = 组内**首条腿**在原序列中的位置（不是最大值那条），保序
 */
export function toBillLegRows(sources: readonly BillLegSource[]): BillLegRowView[] {
  interface Group {
    first: BillLegSource;
    legs: BillLegSource[];
  }
  // 🔴 用 `Map` 保序（同一个 key 只会命中同一个 Group），行序 = 首次出现顺序
  const groups = new Map<string, Group>();
  for (const s of sources) {
    const key = `${s.transactionId}|${s.direction}`;
    const hit = groups.get(key);
    if (hit === undefined) groups.set(key, { first: s, legs: [s] });
    else hit.legs.push(s);
  }

  const rows: BillLegRowView[] = [];
  for (const { first, legs } of groups.values()) {
    const amounts: Cents[] = legs.map((l) => l.amount);
    const amount = sumCents(amounts);
    const merged = legs.length > 1;

    // 分类 / 备注：**全同才给**，否则 `null`（端侧据此显示「拆分 N 项」而不是猜一个出来）
    const firstCat = legs[0]?.categoryId ?? null;
    const sameCategory = legs.every((l) => l.categoryId === firstCat);
    const firstNote = legs[0]?.note ?? null;
    const sameNote = legs.every((l) => l.note === firstNote);

    rows.push({
      transactionId: first.transactionId,
      date: first.date,
      kind: kindOf(first),
      direction: first.direction,
      amount: amount as number,
      categoryId: sameCategory ? firstCat : null,
      label: sameCategory ? (legs[0]?.label ?? null) : null,
      note: sameNote ? firstNote : null,
      legCount: legs.length,
      // 🔴 只有真合并了才出 `children` —— 单腿行带一个空数组既冗余又让端侧分不清
      ...(merged
        ? {
            children: legs.map((l) => ({
              categoryId: l.categoryId,
              label: l.label,
              amount: l.amount as number,
              note: l.note,
            })),
          }
        : {}),
    });
  }
  return rows;
}

/**
 * 🔴 **窗口包含判定**（`AC-B2.29`：端侧不得自行按日期过滤）。
 *
 * 两种窗口形态（都出自 `nucleus/account.ts`，这里只把判定式抽成一处消费）：
 *
 * | 来源 | 区间 | `includeEnd` |
 * |---|---|:---:|
 * | `computeCreditBill`（当期 / 往期） | `[windowStart, windowEnd)` | `false` |
 * | `computeCreditBillHistory`（未出） | `[windowStart, windowEnd]` | `true` |
 *
 * 未出账单右端**含**今天：这一段会在下个账单日成为"当期账单"，今天花的就是它的一部分
 * （若用半开，今天的消费会在两个视图里都看不见）。
 *
 * ⚠️ 本函数与 `computeCreditBill` 内部的 `inWindow` 必须是**同一条表达式** ——
 * 改一处必须改另一处（这是本文件存在的唯一理由）。
 */
export function isInBillWindow(
  window: { windowStart: string; windowEnd: string; includeEnd?: boolean },
  date: string,
): boolean {
  if (date < window.windowStart) return false;
  return window.includeEnd === true ? date <= window.windowEnd : date < window.windowEnd;
}
